import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Button, Checkbox, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
    ModalHeader, ModalOverlay, Stack, Text, useColorModeValue,
} from "@chakra-ui/react";
import { dispatchTransaction } from "../groupsApi";
import { TRANSACTION_SUBJECT_LABELS } from "../groupsConstants";
import { InfoBanner } from "../groupsUi";

/**
 * Builds the initial selection (all eligible members preselected).
 * @param {Array} members - Group members
 * @returns {Object} Map of memberId -> boolean
 */
function buildInitialSelection(members) {
    const sel = {};
    (members || []).filter((m) => m?.user_id).forEach((m) => { sel[m.id] = true; });
    return sel;
}

/**
 * Returns the list of selected member IDs.
 * @param {Object} selection - Selection state
 * @returns {number[]}
 */
function pickSelectedIds(selection) {
    return Object.entries(selection)
        .filter(([, v]) => Boolean(v))
        .map(([k]) => Number(k));
}

/**
 * Submits the dispatch and surfaces toasts.
 * @param {Object} args - Submission args
 */
async function submitDispatch({ groupId, transactionId, selection, setLoading, onClose, onDispatched }) {
    const ids = pickSelectedIds(selection);
    if (ids.length === 0) { toast.error("Sélectionnez au moins un membre."); return; }
    setLoading(true);
    try {
        await dispatchTransaction(groupId, transactionId, { participating_members: ids });
        toast.success("Transaction redispatchée");
        onDispatched?.();
        onClose();
    } catch (error) {
        const msg = error?.response?.data;
        toast.error(typeof msg === "string" ? msg : "Échec du dispatch");
    } finally {
        setLoading(false);
    }
}

/**
 * Header summarizing the transaction being redispatched.
 * @param {Object} props - { transaction, currency }
 */
function TransactionSummary({ transaction, currency }) {
    const amount = Number(transaction?.amount ?? 0).toFixed(2);
    const subject = TRANSACTION_SUBJECT_LABELS[transaction?.subject] || transaction?.subject || "—";
    return (
        <InfoBanner>#{transaction?.id} — {subject} — {amount} {currency}</InfoBanner>
    );
}

/**
 * Member checklist with per-member toggles.
 * @param {Object} props - { members, selection, setSelection }
 */
function MembersChecklist({ members, selection, setSelection }) {
    const eligible = (members || []).filter((m) => m?.user_id);
    const toggle = (id) => setSelection((s) => ({ ...s, [id]: !s[id] }));
    if (eligible.length === 0) {
        return (
            <Text color="red.400" fontSize="0.8rem">
                Aucun membre éligible (compte utilisateur lié requis).
            </Text>
        );
    }
    return (
        <Stack spacing="6px">
            {eligible.map((m) => (
                <Checkbox key={m.id} colorScheme="brand" isChecked={Boolean(selection[m.id])} onChange={() => toggle(m.id)}>
                    <Text fontSize="0.85rem">{m.surname || m.email || `#${m.id}`}{m.is_leader ? " — Leader" : ""}</Text>
                </Checkbox>
            ))}
        </Stack>
    );
}

/**
 * Body of the dispatch dialog.
 * @param {Object} props - { transaction, members, currency, selection, setSelection }
 */
function DialogBody({ transaction, members, currency, selection, setSelection }) {
    const textColor = useColorModeValue("secondaryGray.800", "whiteAlpha.900");
    return (
        <Stack spacing="16px">
            <TransactionSummary transaction={transaction} currency={currency} />
            <Text color={textColor} fontSize="0.8rem">Sélectionner les membres participant à la répartition.</Text>
            <MembersChecklist members={members} selection={selection} setSelection={setSelection} />
        </Stack>
    );
}

/**
 * Dialog allowing admins to redispatch a transaction across selected members.
 * @param {Object} props - { groupId, transaction, members, currency, onClose, onDispatched }
 */
function DispatchTransactionDialog({ groupId, transaction, members, currency, onClose, onDispatched }) {
    const open = Boolean(transaction);
    const [selection, setSelection] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (open) setSelection(buildInitialSelection(members)); }, [open, members]);

    const onSubmit = () => submitDispatch({ groupId, transactionId: transaction?.id, selection, setLoading, onClose, onDispatched });

    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Redispatcher la transaction</ModalHeader>
                <ModalCloseButton />
                <ModalBody><DialogBody transaction={transaction} members={members} currency={currency} selection={selection} setSelection={setSelection} /></ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="brand" onClick={onSubmit} isLoading={loading} loadingText="Dispatch...">Confirmer</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default DispatchTransactionDialog;
